import { currentLocale } from "../lib/i18n";

interface ShopifyAppBridge {
  idToken: () => Promise<string>;
  config?: { shop?: string };
}

declare global {
  interface Window { shopify?: ShopifyAppBridge; }
}

export interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public body?: unknown,
    public retryable = false,
  ) {
    super(`API ${status}: ${detail}`);
  }
}

export class NetworkError extends Error {
  constructor(public retryable: boolean) { super("network_request_failed"); }
}

export function normalizeErrorDetail(text: string): string {
  let parsed: unknown;
  try { parsed = JSON.parse(text); } catch { return text; }
  const detail = (parsed as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (item as { msg?: unknown })?.msg)
      .filter((msg): msg is string => typeof msg === "string")
      .map((msg) => msg.replace(/^Value error,\s*/, ""));
    if (messages.length > 0) return messages.join(" / ");
  }
  return text;
}

async function authHeader(): Promise<Record<string, string>> {
  if (window.shopify?.idToken) {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const token = await Promise.race([
        window.shopify.idToken(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error()), 10_000);
        }),
      ]);
      if (!token) throw new Error();
      return { Authorization: `Bearer ${token}` };
    } catch {
      // App Bridgeの例外・token原文は画面やlogへ出さない。
      throw new ApiError(401, "session_token_unavailable");
    } finally { clearTimeout(timer); }
  }
  const fallback = import.meta.env.VITE_DEV_SESSION_TOKEN;
  return fallback ? { Authorization: `Bearer ${fallback}` } : {};
}

/** 1回だけ送る低層client。認証操作もこれを使い、認証回復へ再帰しない。 */
export async function rawRequest(path: string, opts: ApiOptions = {}): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-DenchoBox-Locale": currentLocale(),
    ...(await authHeader()),
  };
  let url = (import.meta.env.VITE_API_BASE_URL ?? "") + path;
  if (opts.query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(opts.query)) {
      if (value !== undefined && value !== null) params.set(key, String(value));
    }
    if (params.size) url += (url.includes("?") ? "&" : "?") + params.toString();
  }
  const method = opts.method ?? "GET";
  const controller = new AbortController();
  // 既に保存された404/410も避ける。server側のno-storeだけでは既存cacheは消えない。
  const init: RequestInit = { method, headers, signal: controller.signal, cache: "no-store" };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(opts.body);
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      (async () => {
        const response = await fetch(url, init);
        // headersだけでは完了にしない。本文も同じ期限で受け取り、後段のJSON/Blob
        // 読取りは受信済みbufferだけを扱う。POSTの本文喪失も再送へ接続しない。
        const bytes = await response.arrayBuffer();
        if (!response.ok) {
          const text = new TextDecoder().decode(bytes);
          let body: unknown;
          try { body = JSON.parse(text); } catch { /* JSON以外の応答 */ }
          throw new ApiError(response.status, normalizeErrorDetail(text), body,
            method === "GET" && response.status >= 500);
        }
        return new Response([204, 205].includes(response.status) ? null : bytes, {
          status: response.status, statusText: response.statusText, headers: response.headers,
        });
      })(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new NetworkError(method === "GET"));
        }, 30_000);
      }),
    ]);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    // POSTの結果不明をReact QueryのqueryFnからも再送させない。
    throw new NetworkError(method === "GET");
  } finally { clearTimeout(timer); }
}
